import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Template from '../../components/Template';
import api from '../../services/api';
import config from '../../config';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import logoEmpresa from '../../components/logoEmpresaBase64';
import { useAuth } from '../../context/AuthContext';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, ResponsiveContainer
} from 'recharts';

const COLORS = ['#78716c', '#a8a29e', '#57534e', '#d6d3d1', '#44403c', '#e7e5e4', '#292524', '#c4b5fd'];

const formatCurrency = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const formatPercent = (v) => `${Number(v || 0).toFixed(1)}%`;

// ----- Helpers de exportação -----
const addPdfFooter = (doc, userName) => {
  const pageCount = doc.internal.getNumberOfPages();
  const dataEmissao = new Date().toLocaleString('pt-BR');
  const pageHeight = doc.internal.pageSize.height;
  const pageWidth = doc.internal.pageSize.getWidth();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(100);
    doc.text(`Emitido por: ${userName || 'Sistema'}`, 14, pageHeight - 15);
    doc.text(`Data/Hora: ${dataEmissao}`, 14, pageHeight - 10);
    doc.text(`Página ${i} de ${pageCount}`, pageWidth - 20, pageHeight - 10, { align: 'right' });
    if (logoEmpresa) {
      const logoWidth = 40, logoHeight = 12;
      doc.addImage(logoEmpresa, 'PNG', (pageWidth - logoWidth) / 2, pageHeight - 18, logoWidth, logoHeight);
    }
  }
};

const pdfTableStyles = {
  headStyles: { fillColor: [30, 120, 180], textColor: 255, fontStyle: 'bold' },
  alternateRowStyles: { fillColor: [240, 240, 240] },
  theme: 'striped',
  bodyStyles: { valign: 'middle', fontSize: 8 },
  headStyles2: { fontSize: 8 },
};


const KpiCard = ({ title, value, sub, color = 'stone', tooltip }) => {
  const colors = {
    stone: 'bg-stone-50 border-stone-200 text-stone-700',
    red: 'bg-red-50 border-red-200 text-red-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    green: 'bg-green-50 border-green-200 text-green-700',
  };
  return (
    <div className={`rounded-lg border p-4 flex flex-col gap-1 ${colors[color]}`}>
      <div className="flex items-center justify-between gap-1">
        <span className="text-xs font-medium uppercase tracking-wide opacity-70">{title}</span>
        {tooltip && (
          <div className="relative group shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 opacity-40 hover:opacity-70 cursor-help" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
            </svg>
            <div className="absolute right-0 top-5 z-20 hidden group-hover:block w-60 bg-stone-800 text-white text-xs rounded-lg p-3 shadow-xl leading-relaxed">
              {tooltip}
              <div className="absolute -top-1.5 right-1 w-3 h-3 bg-stone-800 rotate-45" />
            </div>
          </div>
        )}
      </div>
      <span className="text-2xl font-bold">{value}</span>
      {sub && <span className="text-xs opacity-60">{sub}</span>}
    </div>
  );
};

// ----- Custom Tooltip -----
const CustomBarTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-stone-200 rounded-lg p-3 shadow-lg text-sm">
        <p className="font-medium text-stone-800 mb-1 max-w-[220px]">{label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.fill || p.color }}>
            {p.name}: {p.name === 'Participação (%)' ? formatPercent(p.value) : formatCurrency(p.value)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const CustomPieTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-stone-200 rounded-lg p-3 shadow-lg text-sm">
        <p className="font-medium text-stone-800">{payload[0].name}</p>
        <p className="text-stone-600">{formatCurrency(payload[0].value)}</p>
        <p className="text-stone-500">{formatPercent(payload[0].payload.percentual)}</p>
      </div>
    );
  }
  return null;
};

// ===== MODAL: AMOSTRAS DO MATERIAL =====
const ModalAmostras = ({ dados, threshold, onClose }) => {
  const mediaPreco = dados.mediaPreco || 0;
  const limiteAlerta = mediaPreco * (1 + threshold / 100);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Painel */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Cabeçalho */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-stone-200 bg-stone-50">
          <div className="flex flex-col gap-0.5 pr-8">
            <h2 className="text-base font-semibold text-stone-800 leading-snug">
              {dados.descricao || 'Material sem descrição'}
            </h2>
            <div className="flex gap-4 text-xs text-stone-500 mt-1">
              {dados.ncm && <span>NCM: <span className="font-mono text-stone-700">{dados.ncm}</span></span>}
              <span>Catálogo ID: <span className="font-mono text-stone-700">{dados.catalogoId}</span></span>
              <span>Total de registros: <span className="font-semibold text-stone-700">{dados.numRegistros}</span></span>
            </div>
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-700 p-1 rounded-lg hover:bg-stone-200 transition-colors shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 px-6 py-4 border-b border-stone-100 bg-white">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="text-xs text-blue-500 font-medium uppercase tracking-wide">Preço médio</div>
            <div className="text-lg font-bold text-blue-700 mt-0.5">{formatCurrency(dados.mediaPreco)}</div>
          </div>
          <div className="bg-stone-50 border border-stone-200 rounded-lg p-3">
            <div className="text-xs text-stone-500 font-medium uppercase tracking-wide">Mínimo</div>
            <div className="text-lg font-bold text-stone-700 mt-0.5">{formatCurrency(dados.minPreco)}</div>
          </div>
          <div className="bg-stone-50 border border-stone-200 rounded-lg p-3">
            <div className="text-xs text-stone-500 font-medium uppercase tracking-wide">Máximo</div>
            <div className="text-lg font-bold text-stone-700 mt-0.5">{formatCurrency(dados.maxPreco)}</div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <div className="text-xs text-amber-500 font-medium uppercase tracking-wide">Limite alerta ({threshold}%)</div>
            <div className="text-lg font-bold text-amber-700 mt-0.5">{formatCurrency(limiteAlerta)}</div>
          </div>
        </div>

        {/* Legenda */}
        <div className="px-6 py-2 text-xs text-stone-400 bg-white border-b border-stone-100">
          Linhas em <span className="text-red-600 font-medium">vermelho</span> indicam preço acima do limite de alerta ({formatCurrency(limiteAlerta)}).
        </div>

        {/* Tabela */}
        <div className="overflow-auto flex-1">
          <table className="w-full text-sm min-w-[860px]">
            <thead className="bg-stone-100 text-stone-600 text-xs uppercase tracking-wide sticky top-0">
              <tr>
                <th className="text-left px-4 py-3">Fornecedor</th>
                <th className="text-left px-4 py-3">CNPJ/CPF</th>
                <th className="text-left px-4 py-3">Obra</th>
                <th className="text-left px-4 py-3">NF / Data</th>
                <th className="text-left px-4 py-3">CFOP</th>
                <th className="text-left px-4 py-3">Unidade</th>
                <th className="text-right px-4 py-3">Preço Unit.</th>
                <th className="text-right px-4 py-3">Qtd</th>
                <th className="text-right px-4 py-3">Total NF Item</th>
                <th className="text-right px-4 py-3">vs Média</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {dados.amostras.map((a) => {
                const acima = a.valorUnitario > limiteAlerta;
                const desvio = mediaPreco > 0 ? ((a.valorUnitario - mediaPreco) / mediaPreco * 100) : 0;
                return (
                  <tr key={a.itemId} className={acima ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-stone-50'}>
                    <td className="px-4 py-2.5 max-w-[160px]">
                      <span title={a.nomeFornecedor} className={`truncate block font-medium ${acima ? 'text-red-800' : 'text-stone-800'}`}>
                        {a.nomeFornecedor || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs text-stone-500">{a.cpfCnpj || '—'}</td>
                    <td className="px-4 py-2.5 text-xs text-stone-600 max-w-[140px]">
                      <span title={a.obraNome} className="truncate block">{a.obraNome || '—'}</span>
                      {a.clienteNome && <span className="text-stone-400">{a.clienteNome}</span>}
                    </td>
                    <td className="px-4 py-2.5 text-xs">
                      {a.nfPdf ? (
                        <a
                          href={config.urlArquivo(a.nfPdf)}
                          target="_blank"
                          rel="noreferrer"
                          className={`flex items-center gap-1 hover:underline font-medium ${acima ? 'text-red-600' : 'text-blue-600'}`}
                          title="Abrir PDF da nota fiscal"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                          </svg>
                          NF {a.nfNumero || '—'}
                        </a>
                      ) : (
                        <div className={acima ? 'text-red-700' : 'text-stone-700'}>NF {a.nfNumero || '—'}</div>
                      )}
                      <div className="text-stone-400">
                        {a.dataEmissao ? new Date(a.dataEmissao).toLocaleDateString('pt-BR') : '—'}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-xs font-mono text-stone-500">{a.cfop || '—'}</td>
                    <td className="px-4 py-2.5 text-xs text-stone-600 font-medium">{a.unidadeMedida || '—'}</td>
                    <td className={`px-4 py-2.5 text-right font-semibold ${acima ? 'text-red-700' : 'text-stone-800'}`}>
                      {formatCurrency(a.valorUnitario)}
                    </td>
                    <td className="px-4 py-2.5 text-right text-stone-600">
                      {a.quantidade != null ? Number(a.quantidade).toLocaleString('pt-BR') : '—'}
                    </td>
                    <td className="px-4 py-2.5 text-right text-stone-600">
                      {a.valorTotal != null ? formatCurrency(a.valorTotal) : '—'}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {mediaPreco > 0 ? (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${
                          acima ? 'bg-red-100 text-red-700' : desvio >= 0 ? 'bg-stone-100 text-stone-600' : 'bg-green-100 text-green-700'
                        }`}>
                          {desvio >= 0 ? '+' : ''}{desvio.toFixed(1)}%
                        </span>
                      ) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {dados.amostras.length === 0 && (
            <p className="text-center text-stone-400 text-sm py-10">Nenhuma amostra encontrada.</p>
          )}
        </div>

        {/* Rodapé */}
        <div className="flex justify-end px-6 py-3 border-t border-stone-200 bg-stone-50">
          <button onClick={onClose} className="px-4 py-2 bg-stone-700 text-white text-sm rounded-lg hover:bg-stone-800">
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

// ===== ABA: MATERIAIS =====
const MateriaisTab = ({ clientes, obras, filtros }) => {
  const { auth } = useAuth();
  const [obraId, setObraId] = useState('');
  const [clienteId, setClienteId] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 30;

  const buscar = useCallback(async (pg = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ pageSize, page: pg, ...filtros });
      if (obraId)    params.set('obraId', obraId);
      if (clienteId) params.set('clienteId', clienteId);
      if (search)    params.set('search', search);
      const resp = await api.get(`/compliance/materiais?${params}`);
      setData(resp.data);
      setPage(pg);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [obraId, clienteId, search, filtros]);

  useEffect(() => { buscar(1); }, [buscar]);

  const totalPages = data ? Math.ceil(data.total / pageSize) : 0;

  // Busca TODOS os registros em lotes de 5000 (máximo suportado pelo backend)
  const fetchAllMateriais = async () => {
    const fetched = [];
    let pg = 1;
    const batchSize = 5000;
    while (true) {
      const p = new URLSearchParams({ pageSize: batchSize, page: pg, ...filtros });
      if (obraId) p.set('obraId', obraId);
      if (clienteId) p.set('clienteId', clienteId);
      if (search) p.set('search', search);
      const resp = await api.get(`/compliance/materiais?${p}`);
      fetched.push(...resp.data.records);
      if (fetched.length >= resp.data.total || !resp.data.records.length) break;
      pg++;
    }
    return fetched;
  };

  const exportarMateriaisPDF = async () => {
    if (!data || exportLoading) return;
    const userName = auth?.user?.name || 'Sistema';
    setExportLoading(true);
    let allRecords = data.records;
    try {
      if (data.total > data.records.length) {
        allRecords = await fetchAllMateriais();
      }
    } catch (e) {
      console.error('Erro ao buscar todos os materiais para PDF:', e);
      alert('Erro ao buscar dados para exportação. Verifique a conexão e tente novamente.');
      setExportLoading(false);
      return;
    }
    try {
      const doc = new jsPDF({ orientation: 'landscape' });
      doc.setFontSize(16);
      doc.text('Compliance — Relatório de Materiais', 14, 20);
      doc.setFontSize(9); doc.setTextColor(120);
      doc.text(`Total: ${data.total.toLocaleString('pt-BR')} materiais  |  Valor total: ${formatCurrency(data.valorTotalGeral)}`, 14, 27);
      doc.setTextColor(0);
      autoTable(doc, {
        startY: 33,
        head: [['Material', 'ID Cat.', 'NCM', 'CFOP', 'Unidade', 'Qtd Total', 'Valor Total', 'Preço Médio', 'Nº Notas', 'Nº Entradas']],
        body: allRecords.map(r => [
          r.descricao || '—',
          r.catalogoId,
          r.ncm || '—',
          r.cfop || '—',
          r.unidadeMedida || '—',
          Number(r.quantidadeTotal).toLocaleString('pt-BR', { maximumFractionDigits: 3 }),
          formatCurrency(r.valorTotal),
          r.mediaUnitario != null ? formatCurrency(r.mediaUnitario) : '—',
          r.numNotas,
          r.numItens,
        ]),
        ...pdfTableStyles,
      });
      addPdfFooter(doc, userName);
      doc.save('compliance-materiais.pdf');
    } catch (e) {
      console.error('Erro ao gerar PDF de materiais:', e);
      alert('Erro ao gerar o PDF. Tente exportar em Excel para grandes volumes de dados.');
    } finally {
      setExportLoading(false);
    }
  };

  const exportarMateriaisExcel = async () => {
    if (!data || exportLoading) return;
    setExportLoading(true);
    let allRecords = data.records;
    try {
      if (data.total > data.records.length) {
        allRecords = await fetchAllMateriais();
      }
    } catch (e) {
      console.error('Erro ao buscar todos os materiais para Excel:', e);
      alert('Erro ao buscar dados para exportação. Verifique a conexão e tente novamente.');
      setExportLoading(false);
      return;
    }
    try {
      const headers = ['Material', 'ID Catálogo', 'NCM', 'CFOP', 'Unidade', 'Qtd Total', 'Valor Total (R$)', 'Preço Médio (R$)', 'Nº Notas', 'Nº Entradas'];
      const rows = allRecords.map(r => [
        r.descricao || '',
        r.catalogoId,
        r.ncm || '',
        r.cfop || '',
        r.unidadeMedida || '',
        Number(r.quantidadeTotal).toLocaleString('pt-BR', { maximumFractionDigits: 3 }),
        formatCurrency(r.valorTotal),
        r.mediaUnitario != null ? formatCurrency(r.mediaUnitario) : '',
        r.numNotas,
        r.numItens,
      ]);
      const ws = XLSX.utils.aoa_to_sheet([
        ['Compliance — Relatório de Materiais'],
        [`Total: ${data.total.toLocaleString('pt-BR')} materiais | Valor total: ${formatCurrency(data.valorTotalGeral)}`],
        [],
        headers,
        ...rows,
      ]);
      ws['!cols'] = [{ wch: 40 }, { wch: 10 }, { wch: 12 }, { wch: 8 }, { wch: 10 }, { wch: 12 }, { wch: 16 }, { wch: 16 }, { wch: 10 }, { wch: 12 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Materiais');
      XLSX.writeFile(wb, 'compliance-materiais.xlsx');
    } catch (e) {
      console.error('Erro ao gerar Excel de materiais:', e);
      alert('Erro ao gerar o arquivo Excel.');
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Filtros */}
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs text-stone-500 mb-1">Obra</label>
          <select
            value={obraId}
            onChange={e => setObraId(e.target.value)}
            className="border border-stone-300 rounded-lg px-3 py-2 text-sm text-stone-700 bg-white min-w-[220px]"
          >
            <option value="">Todas as obras</option>
            {obras.map(o => (
              <option key={o.id} value={o.id}>{o.codigoProjeto} — {o.nomeProjeto}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-stone-500 mb-1">Cliente</label>
          <select
            value={clienteId}
            onChange={e => setClienteId(e.target.value)}
            className="border border-stone-300 rounded-lg px-3 py-2 text-sm text-stone-700 bg-white min-w-[180px]"
          >
            <option value="">Todos os clientes</option>
            {clientes.map(c => (
              <option key={c.id} value={c.id}>{c.nome}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-stone-500 mb-1">Buscar material</label>
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Nome do material…"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') setSearch(searchInput); }}
              className="border border-stone-300 rounded-lg px-3 py-2 text-sm text-stone-700 w-52"
            />
            <button
              onClick={() => setSearch(searchInput)}
              className="px-3 py-2 border border-stone-300 rounded-lg text-stone-600 hover:bg-stone-100"
              title="Pesquisar"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
              </svg>
            </button>
          </div>
        </div>
        {(obraId || clienteId || search) && (
          <button
            onClick={() => { setObraId(''); setClienteId(''); setSearch(''); setSearchInput(''); }}
            className="text-xs text-stone-500 hover:text-stone-700 underline self-end pb-2"
          >
            Limpar filtros
          </button>
        )}
        {data && (
          <div className="flex gap-2 self-end ml-auto">
            <button onClick={exportarMateriaisExcel} disabled={exportLoading} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed">
              {exportLoading ? (
                <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" /></svg>
              )}
              {exportLoading ? 'Carregando…' : 'Excel'}
            </button>
            <button onClick={exportarMateriaisPDF} disabled={exportLoading} className="flex items-center gap-1.5 px-3 py-1.5 bg-stone-600 text-white text-xs font-medium rounded-lg hover:bg-stone-700 disabled:opacity-60 disabled:cursor-not-allowed">
              {exportLoading ? (
                <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>
              )}
              {exportLoading ? 'Carregando…' : 'PDF'}
            </button>
          </div>
        )}
      </div>

      {/* KPIs */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <KpiCard title="Materiais distintos" value={data.total.toLocaleString('pt-BR')} color="stone" />
          <KpiCard title="Valor total" value={formatCurrency(data.valorTotalGeral)} color="stone" sub="soma de todos os itens filtrados" />
          <KpiCard title="Página" value={`${page}/${totalPages || 1}`} color="stone" sub={`${pageSize} itens por página`} />
        </div>
      )}

      {/* Tabela */}
      {loading && (
        <p className="text-stone-400 text-sm text-center py-12">Carregando…</p>
      )}

      {!loading && data && (
        <div className="bg-white border border-stone-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm min-w-[800px]">
            <thead className="bg-stone-100 text-stone-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3">#</th>
                <th className="text-left px-4 py-3">Material</th>
                <th className="text-left px-4 py-3">NCM</th>
                <th className="text-left px-4 py-3">CFOP</th>
                <th className="text-left px-4 py-3">Unidade</th>
                <th className="text-right px-4 py-3">Qtd Total</th>
                <th className="text-right px-4 py-3">Valor Total</th>
                <th className="text-right px-4 py-3">Preço Médio Unit.</th>
                <th className="text-right px-4 py-3">Nº Notas</th>
                <th className="text-right px-4 py-3">Nº Entradas</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {data.records.map((r, i) => (
                <tr key={r.catalogoId} className="hover:bg-stone-50">
                  <td className="px-4 py-3 text-stone-400 font-mono text-xs">{(page - 1) * pageSize + i + 1}</td>
                  <td className="px-4 py-3 max-w-[260px]">
                    <div className="text-stone-800 font-medium truncate" title={r.descricao}>{r.descricao || '—'}</div>
                    <div className="text-stone-400 font-mono text-xs">ID catálogo: {r.catalogoId}</div>
                  </td>
                  <td className="px-4 py-3 text-stone-500 font-mono text-xs">{r.ncm || '—'}</td>
                  <td className="px-4 py-3 text-stone-500 font-mono text-xs">{r.cfop || '—'}</td>
                  <td className="px-4 py-3 text-stone-600 text-xs font-medium">{r.unidadeMedida || '—'}</td>
                  <td className="px-4 py-3 text-right text-stone-700">
                    {r.quantidadeTotal.toLocaleString('pt-BR', { maximumFractionDigits: 3 })}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-stone-800">{formatCurrency(r.valorTotal)}</td>
                  <td className="px-4 py-3 text-right text-stone-600">
                    {r.mediaUnitario != null ? formatCurrency(r.mediaUnitario) : '—'}
                  </td>
                  <td className="px-4 py-3 text-right text-stone-500">{r.numNotas}</td>
                  <td className="px-4 py-3 text-right text-stone-500">{r.numItens}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.records.length === 0 && (
            <p className="text-center text-stone-400 text-sm py-10">Nenhum material encontrado.</p>
          )}
        </div>
      )}

      {/* Paginação */}
      {data && totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-stone-600">
          <span>Total: {data.total.toLocaleString('pt-BR')} materiais</span>
          <div className="flex gap-2">
            <button
              disabled={page <= 1}
              onClick={() => buscar(page - 1)}
              className="px-3 py-1 border border-stone-300 rounded hover:bg-stone-100 disabled:opacity-40"
            >← Anterior</button>
            <span className="px-3 py-1 bg-stone-700 text-white rounded">{page}</span>
            <button
              disabled={page >= totalPages}
              onClick={() => buscar(page + 1)}
              className="px-3 py-1 border border-stone-300 rounded hover:bg-stone-100 disabled:opacity-40"
            >Próximo →</button>
          </div>
        </div>
      )}
    </div>
  );
};

// ===== ABA: MAIORES FORNECEDORES =====
const FornecedoresTab = ({ clientes, obras, filtros }) => {
  const { auth } = useAuth();
  const [modo, setModo] = useState('obra'); // 'obra' | 'cliente'
  const [obraId, setObraId] = useState('');
  const [clienteId, setClienteId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const buscar = useCallback(async () => {
    if (modo === 'obra' && !obraId) return;
    if (modo === 'cliente' && !clienteId) return;
    try {
      setLoading(true);
      const params = new URLSearchParams({ limit: 10, ...filtros });
      let resp;
      if (modo === 'obra') {
        params.set('obraId', obraId);
        resp = await api.get(`/compliance/fornecedores-por-obra?${params}`);
        setData({ ...resp.data, modo: 'obra' });
      } else {
        params.set('clienteId', clienteId);
        resp = await api.get(`/compliance/fornecedores-por-cliente?${params}`);
        setData({ ...resp.data, modo: 'cliente' });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [modo, obraId, clienteId, filtros]);

  useEffect(() => { buscar(); }, [buscar]);

  const chartData = data?.fornecedores?.map(f => ({
    name: f.nomeFornecedor?.length > 28 ? f.nomeFornecedor.substring(0, 28) + '…' : f.nomeFornecedor,
    fullName: f.nomeFornecedor,
    'Valor Total (R$)': f.valorTotal,
    'Participação (%)': f.percentual,
  })) || [];

  const pieData = data?.fornecedores?.map(f => ({
    name: f.nomeFornecedor?.length > 22 ? f.nomeFornecedor.substring(0, 22) + '…' : f.nomeFornecedor,
    value: f.valorTotal,
    percentual: f.percentual,
  })) || [];

  const exportarFornecedoresPDF = () => {
    if (!data || !data.fornecedores?.length) return;
    const userName = auth?.user?.name || 'Sistema';
    const doc = new jsPDF({ orientation: 'landscape' });
    const titulo = modo === 'obra'
      ? `Maiores Fornecedores — ${data.obra?.codigoProjeto || ''} ${data.obra?.nomeProjeto || ''}`
      : `Maiores Fornecedores — Cliente: ${data.cliente?.nome || ''}`;
    doc.setFontSize(15);
    doc.text('Compliance — Maiores Fornecedores', 14, 20);
    doc.setFontSize(9); doc.setTextColor(120);
    doc.text(titulo, 14, 27);
    doc.setTextColor(0);
    const cols = modo === 'obra'
      ? ['#', 'Fornecedor', 'CNPJ/CPF', 'Nº Notas', 'Valor Total (R$)', 'Participação (%)']
      : ['#', 'Fornecedor', 'CNPJ/CPF', 'Nº Notas', 'Nº Obras', 'Valor Total (R$)', 'Participação (%)'];
    const body = data.fornecedores.map((f, i) => modo === 'obra'
      ? [i + 1, f.nomeFornecedor, f.cpfCnpj, f.numNotas, formatCurrency(f.valorTotal), formatPercent(f.percentual)]
      : [i + 1, f.nomeFornecedor, f.cpfCnpj, f.numNotas, f.numObras, formatCurrency(f.valorTotal), formatPercent(f.percentual)]
    );
    autoTable(doc, { startY: 33, head: [cols], body, ...pdfTableStyles });
    addPdfFooter(doc, userName);
    doc.save('compliance-fornecedores.pdf');
  };

  const exportarFornecedoresExcel = () => {
    if (!data || !data.fornecedores?.length) return;
    const titulo = modo === 'obra'
      ? `Maiores Fornecedores — ${data.obra?.codigoProjeto || ''} ${data.obra?.nomeProjeto || ''}`
      : `Maiores Fornecedores — Cliente: ${data.cliente?.nome || ''}`;
    const cols = modo === 'obra'
      ? ['#', 'Fornecedor', 'CNPJ/CPF', 'Nº Notas', 'Valor Total (R$)', 'Participação (%)']
      : ['#', 'Fornecedor', 'CNPJ/CPF', 'Nº Notas', 'Nº Obras', 'Valor Total (R$)', 'Participação (%)'];
    const rows = data.fornecedores.map((f, i) => modo === 'obra'
      ? [i + 1, f.nomeFornecedor, f.cpfCnpj, f.numNotas, formatCurrency(f.valorTotal), formatPercent(f.percentual)]
      : [i + 1, f.nomeFornecedor, f.cpfCnpj, f.numNotas, f.numObras, formatCurrency(f.valorTotal), formatPercent(f.percentual)]
    );
    const ws = XLSX.utils.aoa_to_sheet([
      ['Compliance — Maiores Fornecedores'],
      [titulo],
      [],
      cols,
      ...rows,
    ]);
    ws['!cols'] = [{ wch: 4 }, { wch: 40 }, { wch: 18 }, { wch: 10 }, ...(modo === 'cliente' ? [{ wch: 10 }] : []), { wch: 16 }, { wch: 14 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Fornecedores');
    XLSX.writeFile(wb, 'compliance-fornecedores.xlsx');
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Controles */}
      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex rounded-lg border border-stone-300 overflow-hidden text-sm">
          <button
            onClick={() => setModo('obra')}
            className={`px-4 py-2 ${modo === 'obra' ? 'bg-stone-700 text-white' : 'bg-white text-stone-600 hover:bg-stone-50'}`}
          >Por Obra</button>
          <button
            onClick={() => setModo('cliente')}
            className={`px-4 py-2 ${modo === 'cliente' ? 'bg-stone-700 text-white' : 'bg-white text-stone-600 hover:bg-stone-50'}`}
          >Por Cliente</button>
        </div>

        {modo === 'obra' ? (
          <select
            value={obraId}
            onChange={e => setObraId(e.target.value)}
            className="border border-stone-300 rounded-lg px-3 py-2 text-sm text-stone-700 bg-white min-w-[220px]"
          >
            <option value="">Selecione uma obra…</option>
            {obras.map(o => (
              <option key={o.id} value={o.id}>{o.codigoProjeto} — {o.nomeProjeto}</option>
            ))}
          </select>
        ) : (
          <select
            value={clienteId}
            onChange={e => setClienteId(e.target.value)}
            className="border border-stone-300 rounded-lg px-3 py-2 text-sm text-stone-700 bg-white min-w-[200px]"
          >
            <option value="">Selecione um cliente…</option>
            {clientes.map(c => (
              <option key={c.id} value={c.id}>{c.nome}</option>
            ))}
          </select>
        )}

        <button
          onClick={buscar}
          disabled={loading || (modo === 'obra' ? !obraId : !clienteId)}
          className="px-4 py-2 bg-stone-700 text-white text-sm rounded-lg hover:bg-stone-800 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Buscando…' : 'Buscar'}
        </button>
        {data && (
          <div className="flex gap-2 ml-auto">
            <button onClick={exportarFornecedoresExcel} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" /></svg>
              Excel
            </button>
            <button onClick={exportarFornecedoresPDF} className="flex items-center gap-1.5 px-3 py-1.5 bg-stone-600 text-white text-xs font-medium rounded-lg hover:bg-stone-700">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>
              PDF
            </button>
          </div>
        )}
      </div>

      {data && (
        <>
          {/* Título */}
          <div className="text-sm text-stone-500">
            {data.modo === 'obra'
              ? <><span className="font-medium text-stone-700">{data.obra?.nomeProjeto}</span> — Cliente: {data.obra?.cliente?.nome || '—'}</>
              : <><span className="font-medium text-stone-700">{data.cliente?.nome}</span> — Total investido: {formatCurrency(data.valorTotal)}</>
            }
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Gráfico de barras */}
            <div className="bg-white border border-stone-200 rounded-xl p-4">
              <h3 className="text-sm font-medium text-stone-700 mb-4">Valor por Fornecedor</h3>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData} layout="vertical" margin={{ left: 8, right: 30, top: 4, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e7e5e4" />
                    <XAxis type="number" tickFormatter={v => `R$ ${(v / 1000).toFixed(0)}k`}
                      tick={{ fontSize: 11, fill: '#78716c' }} />
                    <YAxis type="category" dataKey="name" width={130}
                      tick={{ fontSize: 11, fill: '#57534e' }} />
                    <Tooltip content={<CustomBarTooltip />} />
                    <Bar dataKey="Valor Total (R$)" fill="#78716c" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="text-stone-400 text-sm text-center py-8">Sem dados</p>}
            </div>

            {/* Pizza de concentração */}
            <div className="bg-white border border-stone-200 rounded-xl p-4">
              <h3 className="text-sm font-medium text-stone-700 mb-4">Distribuição Percentual</h3>
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" outerRadius={100}
                      dataKey="value" nameKey="name" label={({ name, percentual }) => `${formatPercent(percentual)}`}
                      labelLine={false}>
                      {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip content={<CustomPieTooltip />} />
                    <Legend iconType="circle" iconSize={8} formatter={(v) => <span className="text-xs text-stone-600">{v}</span>} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <p className="text-stone-400 text-sm text-center py-8">Sem dados</p>}
            </div>
          </div>

          {/* Tabela */}
          <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-stone-100 text-stone-600 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-4 py-3">#</th>
                  <th className="text-left px-4 py-3">Fornecedor</th>
                  <th className="text-left px-4 py-3">CNPJ/CPF</th>
                  <th className="text-right px-4 py-3">Notas</th>
                  {data.modo === 'cliente' && <th className="text-right px-4 py-3">Obras</th>}
                  <th className="text-right px-4 py-3">Valor Total</th>
                  <th className="text-right px-4 py-3">Participação</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {data.fornecedores.map((f, i) => (
                  <tr key={f.fornecedorId} className="hover:bg-stone-50">
                    <td className="px-4 py-3 text-stone-400 font-mono">{i + 1}</td>
                    <td className="px-4 py-3 text-stone-800 font-medium">{f.nomeFornecedor}</td>
                    <td className="px-4 py-3 text-stone-500 font-mono text-xs">{f.cpfCnpj}</td>
                    <td className="px-4 py-3 text-right text-stone-600">{f.numNotas}</td>
                    {data.modo === 'cliente' && <td className="px-4 py-3 text-right text-stone-600">{f.numObras}</td>}
                    <td className="px-4 py-3 text-right font-medium text-stone-800">{formatCurrency(f.valorTotal)}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium
                        ${f.percentual >= 50 ? 'bg-red-100 text-red-700' :
                          f.percentual >= 30 ? 'bg-amber-100 text-amber-700' :
                          'bg-stone-100 text-stone-600'}`}>
                        {formatPercent(f.percentual)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data.fornecedores.length === 0 && (
              <p className="text-center text-stone-400 text-sm py-8">Nenhum fornecedor encontrado.</p>
            )}
          </div>
        </>
      )}

      {!data && (
        <div className="text-center text-stone-400 py-16 text-sm">
          Selecione uma {modo === 'obra' ? 'obra' : 'cliente'} e clique em Buscar para ver os dados.
        </div>
      )}
    </div>
  );
};

// ===== ABA: SUPERFATURAMENTO =====
const SuperfaturamentoTab = ({ filtros }) => {
  const { auth } = useAuth();
  const [threshold, setThreshold] = useState(20);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [modalAmostras, setModalAmostras] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  const abrirAmostras = useCallback(async (r) => {
    if (!r.catalogoId) return;
    try {
      setModalLoading(true);
      const resp = await api.get(`/compliance/amostras-material?catalogoId=${r.catalogoId}`);
      setModalAmostras(resp.data);
    } catch (e) {
      console.error(e);
    } finally {
      setModalLoading(false);
    }
  }, []);

  const buscar = useCallback(async (pg = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        threshold,
        page: pg,
        pageSize,
        ...filtros,
      });
      const resp = await api.get(`/compliance/superfaturamento?${params}`);
      setData(resp.data);
      setPage(pg);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [threshold, filtros]);

  useEffect(() => { buscar(1); }, [buscar]);

  const totalPages = data ? Math.ceil(data.total / pageSize) : 0;
  const totalExcesso = data?.records?.reduce((s, r) => s + (r.valorExcesso || 0), 0) || 0;

  const exportarSuperfaturamentoPDF = async () => {
    if (!data) return;
    const userName = auth?.user?.name || 'Sistema';
    let allRecords = data.records;
    if (data.total > pageSize) {
      try {
        const params = new URLSearchParams({ threshold, page: 1, pageSize: 1000, ...filtros });
        const resp = await api.get(`/compliance/superfaturamento?${params}`);
        allRecords = resp.data.records;
      } catch (e) { /* usa página atual */ }
    }
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFontSize(15);
    doc.text('Compliance — Superfaturamento por Preço', 14, 20);
    doc.setFontSize(9); doc.setTextColor(120);
    doc.text(`Desvio mínimo: +${threshold}%  |  Alertas: ${data.total}  |  Excesso estimado: ${formatCurrency(totalExcesso)}`, 14, 27);
    doc.setTextColor(0);
    autoTable(doc, {
      startY: 33,
      head: [['Material', 'NCM', 'Fornecedor', 'Obra', 'Preço Pago', 'Média', 'Desvio (%)', 'Excesso (R$)', 'Amostras', 'NF', 'Data']],
      body: allRecords.map(r => [
        r.catalogoDescricao || '—',
        r.ncm || '—',
        r.nomeFornecedor || '—',
        r.obraNome || '—',
        formatCurrency(r.valorUnitario),
        formatCurrency(r.mediaPreco),
        `+${formatPercent(r.percentualDesvio)}`,
        formatCurrency(r.valorExcesso),
        r.numRegistros,
        r.nfNumero || '—',
        r.dataEmissao ? new Date(r.dataEmissao).toLocaleDateString('pt-BR') : '—',
      ]),
      ...pdfTableStyles,
    });
    addPdfFooter(doc, userName);
    doc.save('compliance-superfaturamento.pdf');
  };

  const exportarSuperfaturamentoExcel = async () => {
    if (!data) return;
    let allRecords = data.records;
    if (data.total > pageSize) {
      try {
        const params = new URLSearchParams({ threshold, page: 1, pageSize: 1000, ...filtros });
        const resp = await api.get(`/compliance/superfaturamento?${params}`);
        allRecords = resp.data.records;
      } catch (e) { /* usa página atual */ }
    }
    const headers = ['Material', 'NCM', 'Fornecedor', 'Obra', 'Preço Pago (R$)', 'Média (R$)', 'Desvio (%)', 'Excesso (R$)', 'Nº Amostras', 'NF', 'Data Emissão'];
    const rows = allRecords.map(r => [
      r.catalogoDescricao || '',
      r.ncm || '',
      r.nomeFornecedor || '',
      r.obraNome || '',
      formatCurrency(r.valorUnitario),
      formatCurrency(r.mediaPreco),
      `+${formatPercent(r.percentualDesvio)}`,
      formatCurrency(r.valorExcesso),
      r.numRegistros,
      r.nfNumero || '',
      r.dataEmissao ? new Date(r.dataEmissao).toLocaleDateString('pt-BR') : '',
    ]);
    const ws = XLSX.utils.aoa_to_sheet([
      ['Compliance — Superfaturamento por Preço'],
      [`Desvio mínimo: +${threshold}% | Alertas: ${data.total} | Excesso estimado: ${formatCurrency(totalExcesso)}`],
      [],
      headers,
      ...rows,
    ]);
    ws['!cols'] = [{ wch: 35 }, { wch: 12 }, { wch: 30 }, { wch: 25 }, { wch: 14 }, { wch: 14 }, { wch: 10 }, { wch: 14 }, { wch: 10 }, { wch: 10 }, { wch: 14 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Superfaturamento');
    XLSX.writeFile(wb, 'compliance-superfaturamento.xlsx');
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Filtros */}
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs text-stone-500 mb-1">Desvio mínimo (%)</label>
          <div className="flex items-center gap-2">
            <input
              type="number" min={1} max={500} step={5}
              value={threshold}
              onChange={e => setThreshold(Number(e.target.value))}
              className="border border-stone-300 rounded-lg px-3 py-2 text-sm w-24 text-stone-700"
            />
            <span className="text-xs text-stone-400">acima da média</span>
          </div>
        </div>
        <button
          onClick={() => buscar(1)}
          disabled={loading}
          className="px-4 py-2 bg-stone-700 text-white text-sm rounded-lg hover:bg-stone-800 disabled:opacity-50 self-end"
        >
          {loading ? 'Buscando…' : 'Analisar'}
        </button>
        {data && (
          <div className="flex gap-2 self-end ml-auto">
            <button onClick={exportarSuperfaturamentoExcel} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" /></svg>
              Excel
            </button>
            <button onClick={exportarSuperfaturamentoPDF} className="flex items-center gap-1.5 px-3 py-1.5 bg-stone-600 text-white text-xs font-medium rounded-lg hover:bg-stone-700">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>
              PDF
            </button>
          </div>
        )}
      </div>

      {/* Explicação da lógica */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-xs text-amber-800">
        <strong>Como funciona:</strong> O sistema calcula o preço médio de cada material (por código de catálogo)
        com base em todas as notas fiscais do tenant que possuem pelo menos 2 registros do mesmo item.
        São sinalizados os itens com preço unitário <strong>{threshold}% acima da média</strong> calculada.
        Quanto mais notas houver de um mesmo material, mais confiável é a comparação.
      </div>

      {data && (
        <>
          {/* Resumo */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard title="Alertas encontrados" value={data.total} color={data.total > 0 ? 'red' : 'green'} />
            <KpiCard title="Excesso estimado" value={formatCurrency(totalExcesso)} color={totalExcesso > 0 ? 'amber' : 'green'} />
            <KpiCard title="Threshold" value={`+${threshold}%`} color="stone" sub="desvio mínimo configurado" />
            <KpiCard
              title="Página"
              value={`${page}/${totalPages || 1}`}
              color="stone"
              sub={`${pageSize} itens por página`}
            />
          </div>

          {/* Tabela */}
          <div className="bg-white border border-stone-200 rounded-xl overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]">
              <thead className="bg-stone-100 text-stone-600 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-4 py-3">Material</th>
                  <th className="text-left px-4 py-3">NCM</th>
                  <th className="text-left px-4 py-3">Fornecedor</th>
                  <th className="text-left px-4 py-3">Obra</th>
                  <th className="text-right px-4 py-3">Preço Pago</th>
                  <th className="text-right px-4 py-3">Média</th>
                  <th className="text-right px-4 py-3">Desvio</th>
                  <th className="text-right px-4 py-3">Excesso</th>
                  <th className="text-right px-4 py-3">Qtd amostras</th>
                  <th className="text-left px-4 py-3">NF / Data</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {data.records.map((r) => (
                  <tr key={r.itemId} className="hover:bg-red-50">
                    <td className="px-4 py-3 text-stone-800 max-w-[180px]">
                      <span title={r.catalogoDescricao} className="truncate block">
                        {r.catalogoDescricao || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-stone-500 font-mono text-xs">{r.ncm || '—'}</td>
                    <td className="px-4 py-3 text-stone-700 max-w-[150px]">
                      <span title={r.nomeFornecedor} className="truncate block">
                        {r.nomeFornecedor || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-stone-600 max-w-[140px]">
                      <span title={r.obraNome} className="truncate block text-xs">
                        {r.obraNome || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-stone-800">{formatCurrency(r.valorUnitario)}</td>
                    <td className="px-4 py-3 text-right text-stone-500">{formatCurrency(r.mediaPreco)}</td>
                    <td className="px-4 py-3 text-right">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700">
                        +{formatPercent(r.percentualDesvio)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-red-600 font-medium">{formatCurrency(r.valorExcesso)}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => abrirAmostras(r)}
                        disabled={modalLoading || !r.catalogoId}
                        title="Ver todas as amostras deste material"
                        className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {modalLoading ? '…' : r.numRegistros}
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                        </svg>
                      </button>
                    </td>
                    <td className="px-4 py-3 text-stone-500 text-xs">
                      <div>NF {r.nfNumero}</div>
                      <div className="text-stone-400">
                        {r.dataEmissao ? new Date(r.dataEmissao).toLocaleDateString('pt-BR') : '—'}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data.records.length === 0 && (
              <p className="text-center text-stone-400 text-sm py-10">
                Nenhum alerta encontrado com desvio acima de {threshold}%.
              </p>
            )}
          </div>

          {/* Paginação */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between text-sm text-stone-600">
              <span>Total: {data.total} itens</span>
              <div className="flex gap-2">
                <button
                  disabled={page <= 1}
                  onClick={() => buscar(page - 1)}
                  className="px-3 py-1 border border-stone-300 rounded hover:bg-stone-100 disabled:opacity-40"
                >← Anterior</button>
                <span className="px-3 py-1 bg-stone-700 text-white rounded">{page}</span>
                <button
                  disabled={page >= totalPages}
                  onClick={() => buscar(page + 1)}
                  className="px-3 py-1 border border-stone-300 rounded hover:bg-stone-100 disabled:opacity-40"
                >Próximo →</button>
              </div>
            </div>
          )}
        </>
      )}

      {!data && (
        <div className="text-center text-stone-400 py-16 text-sm">
          Configure o threshold e clique em Analisar para detectar possíveis superfaturamentos.
        </div>
      )}

      {/* Modal de amostras do material */}
      {modalAmostras && (
        <ModalAmostras
          dados={modalAmostras}
          threshold={threshold}
          onClose={() => setModalAmostras(null)}
        />
      )}
    </div>
  );
};

// ===== ABA: CONCENTRAÇÃO DE FORNECEDOR =====
const ConcentracaoTab = ({ filtros }) => {
  const { auth } = useAuth();
  const [threshold, setThreshold] = useState(50);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const buscar = useCallback(async (pg = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ threshold, page: pg, pageSize, ...filtros });
      const resp = await api.get(`/compliance/concentracao-fornecedor?${params}`);
      setData(resp.data);
      setPage(pg);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [threshold, filtros]);

  useEffect(() => { buscar(1); }, [buscar]);

  const totalPages = data ? Math.ceil(data.total / pageSize) : 0;

  const exportarConcentracaoPDF = async () => {
    if (!data) return;
    const userName = auth?.user?.name || 'Sistema';
    let allRecords = data.records;
    if (data.total > pageSize) {
      try {
        const params = new URLSearchParams({ threshold, page: 1, pageSize: 1000, ...filtros });
        const resp = await api.get(`/compliance/concentracao-fornecedor?${params}`);
        allRecords = resp.data.records;
      } catch (e) { /* usa página atual */ }
    }
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFontSize(15);
    doc.text('Compliance — Concentração de Fornecedor', 14, 20);
    doc.setFontSize(9); doc.setTextColor(120);
    doc.text(`Concentração mínima: ≥${threshold}%  |  Ocorrências: ${data.total}`, 14, 27);
    doc.setTextColor(0);
    autoTable(doc, {
      startY: 33,
      head: [['Obra', 'Cód.', 'Cliente', 'Fornecedor', 'CNPJ/CPF', 'Valor Forn. (R$)', 'Total Obra (R$)', 'Notas', 'Concentração (%)']],
      body: allRecords.map(r => [
        r.obraNome || '—',
        r.codigoProjeto || '—',
        r.clienteNome || '—',
        r.nomeFornecedor || '—',
        r.cpfCnpj || '—',
        formatCurrency(r.valorFornecedor),
        formatCurrency(r.totalObra),
        r.numNotas,
        formatPercent(r.percentualConcentracao),
      ]),
      ...pdfTableStyles,
    });
    addPdfFooter(doc, userName);
    doc.save('compliance-concentracao.pdf');
  };

  const exportarConcentracaoExcel = async () => {
    if (!data) return;
    let allRecords = data.records;
    if (data.total > pageSize) {
      try {
        const params = new URLSearchParams({ threshold, page: 1, pageSize: 1000, ...filtros });
        const resp = await api.get(`/compliance/concentracao-fornecedor?${params}`);
        allRecords = resp.data.records;
      } catch (e) { /* usa página atual */ }
    }
    const headers = ['Obra', 'Código Projeto', 'Cliente', 'Fornecedor', 'CNPJ/CPF', 'Valor Fornecedor (R$)', 'Total Obra (R$)', 'Nº Notas', 'Concentração (%)'];
    const rows = allRecords.map(r => [
      r.obraNome || '',
      r.codigoProjeto || '',
      r.clienteNome || '',
      r.nomeFornecedor || '',
      r.cpfCnpj || '',
      formatCurrency(r.valorFornecedor),
      formatCurrency(r.totalObra),
      r.numNotas,
      formatPercent(r.percentualConcentracao),
    ]);
    const ws = XLSX.utils.aoa_to_sheet([
      ['Compliance — Concentração de Fornecedor'],
      [`Concentração mínima: ≥${threshold}% | Ocorrências: ${data.total}`],
      [],
      headers,
      ...rows,
    ]);
    ws['!cols'] = [{ wch: 30 }, { wch: 14 }, { wch: 22 }, { wch: 35 }, { wch: 18 }, { wch: 18 }, { wch: 16 }, { wch: 8 }, { wch: 14 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Concentração');
    XLSX.writeFile(wb, 'compliance-concentracao.xlsx');
  };

  const getBadgeColor = (pct) => {
    if (pct >= 80) return 'bg-red-100 text-red-700';
    if (pct >= 65) return 'bg-orange-100 text-orange-700';
    return 'bg-amber-100 text-amber-700';
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs text-stone-500 mb-1">Concentração mínima (%)</label>
          <div className="flex items-center gap-2">
            <input
              type="number" min={1} max={100} step={5}
              value={threshold}
              onChange={e => setThreshold(Number(e.target.value))}
              className="border border-stone-300 rounded-lg px-3 py-2 text-sm w-24 text-stone-700"
            />
            <span className="text-xs text-stone-400">do gasto da obra</span>
          </div>
        </div>
        <button
          onClick={() => buscar(1)}
          disabled={loading}
          className="px-4 py-2 bg-stone-700 text-white text-sm rounded-lg hover:bg-stone-800 disabled:opacity-50 self-end"
        >
          {loading ? 'Buscando…' : 'Analisar'}
        </button>
        {data && (
          <div className="flex gap-2 self-end ml-auto">
            <button onClick={exportarConcentracaoExcel} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" /></svg>
              Excel
            </button>
            <button onClick={exportarConcentracaoPDF} className="flex items-center gap-1.5 px-3 py-1.5 bg-stone-600 text-white text-xs font-medium rounded-lg hover:bg-stone-700">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>
              PDF
            </button>
          </div>
        )}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-xs text-blue-800">
        <strong>O que é analisado:</strong> Obras onde um único fornecedor representa{' '}
        <strong>{threshold}% ou mais</strong> do valor total de NF-e. Alta concentração pode indicar
        direcionamento de compras, falta de concorrência ou dependência excessiva de um único fornecedor.
      </div>

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <KpiCard title="Obras com concentração" value={data.total} color={data.total > 0 ? 'amber' : 'green'} />
            <KpiCard title="Threshold" value={`≥${threshold}%`} color="stone" sub="concentração mínima" />
          </div>

          <div className="bg-white border border-stone-200 rounded-xl overflow-x-auto">
            <table className="w-full text-sm min-w-[750px]">
              <thead className="bg-stone-100 text-stone-600 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-4 py-3">Obra</th>
                  <th className="text-left px-4 py-3">Cliente</th>
                  <th className="text-left px-4 py-3">Fornecedor</th>
                  <th className="text-left px-4 py-3">CNPJ/CPF</th>
                  <th className="text-right px-4 py-3">Valor Forn.</th>
                  <th className="text-right px-4 py-3">Total Obra</th>
                  <th className="text-right px-4 py-3">Notas</th>
                  <th className="text-right px-4 py-3">Concentração</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {data.records.map((r, i) => (
                  <tr key={`${r.obraId}-${r.fornecedorId}-${i}`} className="hover:bg-amber-50">
                    <td className="px-4 py-3">
                      <div className="text-stone-800 font-medium text-xs">{r.codigoProjeto}</div>
                      <div className="text-stone-500 text-xs max-w-[160px] truncate" title={r.obraNome}>{r.obraNome}</div>
                    </td>
                    <td className="px-4 py-3 text-stone-600 text-xs max-w-[120px] truncate" title={r.clienteNome}>
                      {r.clienteNome || '—'}
                    </td>
                    <td className="px-4 py-3 text-stone-800 max-w-[160px]">
                      <span className="truncate block text-xs" title={r.nomeFornecedor}>{r.nomeFornecedor}</span>
                    </td>
                    <td className="px-4 py-3 text-stone-500 font-mono text-xs">{r.cpfCnpj}</td>
                    <td className="px-4 py-3 text-right font-medium text-stone-800">{formatCurrency(r.valorFornecedor)}</td>
                    <td className="px-4 py-3 text-right text-stone-500">{formatCurrency(r.totalObra)}</td>
                    <td className="px-4 py-3 text-right text-stone-500">{r.numNotas}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${getBadgeColor(r.percentualConcentracao)}`}>
                        {formatPercent(r.percentualConcentracao)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data.records.length === 0 && (
              <p className="text-center text-stone-400 text-sm py-10">
                Nenhuma obra com concentração acima de {threshold}%.
              </p>
            )}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between text-sm text-stone-600">
              <span>Total: {data.total} ocorrências</span>
              <div className="flex gap-2">
                <button disabled={page <= 1} onClick={() => buscar(page - 1)}
                  className="px-3 py-1 border border-stone-300 rounded hover:bg-stone-100 disabled:opacity-40">← Anterior</button>
                <span className="px-3 py-1 bg-stone-700 text-white rounded">{page}</span>
                <button disabled={page >= totalPages} onClick={() => buscar(page + 1)}
                  className="px-3 py-1 border border-stone-300 rounded hover:bg-stone-100 disabled:opacity-40">Próximo →</button>
              </div>
            </div>
          )}
        </>
      )}

      {!data && (
        <div className="text-center text-stone-400 py-16 text-sm">
          Configure o threshold e clique em Analisar.
        </div>
      )}
    </div>
  );
};

// ===== PÁGINA PRINCIPAL =====
const Compliance = () => {
  const { auth } = useAuth();
  const [abaAtiva, setAbaAtiva] = useState('dashboard');
  const [clientes, setClientes] = useState([]);
  const [obras, setObras] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [kpisLoading, setKpisLoading] = useState(false);

  // Filtros globais (período, cliente)
  const [filtroCliente, setFiltroCliente] = useState('');
  const [filtroDataInicial, setFiltroDataInicial] = useState('');
  const [filtroDataFinal, setFiltroDataFinal] = useState('');

  const filtros = useMemo(() => {
    const f = {};
    if (filtroCliente)     f.cliente = filtroCliente;
    if (filtroDataInicial) f.dataInicial = filtroDataInicial;
    if (filtroDataFinal)   f.dataFinal = filtroDataFinal;
    return f;
  }, [filtroCliente, filtroDataInicial, filtroDataFinal]);

  useEffect(() => {
    const fetchFiltros = async () => {
      try {
        const [cl, ob] = await Promise.all([
          api.get('/compliance/clientes-lista'),
          api.get('/compliance/obras-lista'),
        ]);
        setClientes(cl.data);
        setObras(ob.data);
      } catch (e) {
        console.error(e);
      }
    };
    fetchFiltros();
  }, []);

  useEffect(() => {
    if (abaAtiva !== 'dashboard') return;
    const fetchKpis = async () => {
      try {
        setKpisLoading(true);
        const params = new URLSearchParams(filtros);
        const resp = await api.get(`/compliance/dashboard?${params}`);
        setKpis(resp.data);
      } catch (e) {
        console.error(e);
      } finally {
        setKpisLoading(false);
      }
    };
    fetchKpis();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abaAtiva, filtroCliente, filtroDataInicial, filtroDataFinal]);

  const abas = [
    { id: 'dashboard',        label: 'Dashboard' },
    { id: 'materiais',        label: 'Materiais' },
    { id: 'fornecedores',     label: 'Maiores Fornecedores' },
    { id: 'superfaturamento', label: 'Superfaturamento' },
    { id: 'concentracao',     label: 'Concentração' },
  ];

  const exportarDashboardPDF = () => {
    if (!kpis) return;
    const doc = new jsPDF();
    const userName = auth?.user?.name || 'Sistema';
    doc.setFontSize(16);
    doc.text('Compliance — Resumo Executivo', 14, 20);
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text(`Gerado em: ${new Date().toLocaleString('pt-BR')}`, 14, 27);
    doc.setTextColor(0);
    autoTable(doc, {
      startY: 33,
      head: [['Indicador', 'Valor']],
      body: [
        ['Alertas de Superfaturamento', String(kpis.totalAlertasSuperfaturamento)],
        ['Fornecedores Alertados', String(kpis.fornecedoresAlertados)],
        ['Obras com Alertas', String(kpis.obrasAlertadas)],
        ['Excesso Estimado (R$)', formatCurrency(kpis.valorExcessoEstimado)],
        ['Obras c/ Alta Concentração', String(kpis.obrasComConcentracao)],
        ['Threshold Superfaturamento', `+${kpis.thresholdSuperfaturamento}%`],
        ['Threshold Concentração', `≥${kpis.thresholdConcentracao}%`],
      ],
      ...pdfTableStyles,
    });
    addPdfFooter(doc, userName);
    doc.save('compliance-dashboard.pdf');
  };

  const exportarDashboardExcel = () => {
    if (!kpis) return;

    const rows = [
      ['Compliance — Resumo Executivo'],
      [`Gerado em: ${new Date().toLocaleString('pt-BR')}`],
      [],
      ['Indicador', 'Valor'],
      ['Alertas de Superfaturamento', kpis.totalAlertasSuperfaturamento],
      ['Fornecedores Alertados', kpis.fornecedoresAlertados],
      ['Obras com Alertas', kpis.obrasAlertadas],
      ['Excesso Estimado (R$)', formatCurrency(kpis.valorExcessoEstimado)],
      ['Obras c/ Alta Concentração', kpis.obrasComConcentracao],
      ['Threshold Superfaturamento (%)', kpis.thresholdSuperfaturamento],
      ['Threshold Concentração (%)', kpis.thresholdConcentracao],
    ];

    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [{ wch: 38 }, { wch: 22 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Dashboard');
    XLSX.writeFile(wb, 'compliance-dashboard.xlsx');
  };

  return (
    <Template>
      <div className="w-full px-4 py-6 flex flex-col gap-6">
        {/* Cabeçalho */}
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl text-stone-800 font-medium">Compliance</h1>
          <p className="text-sm text-stone-500">
            Análise de riscos, concentração de fornecedores e detecção de superfaturamento em obras e NF-e.
          </p>
        </div>

        {/* Filtros Globais */}
        <div className="bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-stone-400" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 01-.659 1.591l-5.432 5.432a2.25 2.25 0 00-.659 1.591v2.927a2.25 2.25 0 01-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 00-.659-1.591L3.659 7.409A2.25 2.25 0 013 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0112 3z" />
              </svg>
              <span className="text-xs font-medium text-stone-500 uppercase tracking-wide">Filtros globais</span>
              <span className="text-xs text-stone-400">— aplicados em todas as abas</span>
            </div>
            {(filtroCliente || filtroDataInicial || filtroDataFinal) && (
              <button
                onClick={() => { setFiltroCliente(''); setFiltroDataInicial(''); setFiltroDataFinal(''); }}
                className="text-xs text-stone-500 hover:text-stone-700 hover:bg-stone-200 rounded px-2 py-0.5 transition-colors"
              >
                Limpar filtros
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-4 items-end">
            {['materiais', 'fornecedores'].includes(abaAtiva) ? (
              <div className="flex items-center gap-2 bg-stone-100 border border-stone-200 rounded-lg px-3 py-2 text-xs text-stone-500">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-stone-400 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
                </svg>
                Filtro de <strong className="font-medium text-stone-600">Cliente / Obra</strong> disponível na própria aba
              </div>
            ) : (
              <div>
                <label className="block text-xs text-stone-500 mb-1">Cliente</label>
                <select
                  value={filtroCliente}
                  onChange={e => setFiltroCliente(e.target.value)}
                  className="border border-stone-300 rounded-lg px-3 py-2 text-sm text-stone-700 bg-white min-w-[180px]"
                >
                  <option value="">Todos os clientes</option>
                  {clientes.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className="block text-xs text-stone-500 mb-1">Data inicial</label>
              <input type="date" value={filtroDataInicial} onChange={e => setFiltroDataInicial(e.target.value)}
                className="border border-stone-300 rounded-lg px-3 py-2 text-sm text-stone-700" />
            </div>
            <div>
              <label className="block text-xs text-stone-500 mb-1">Data final</label>
              <input type="date" value={filtroDataFinal} onChange={e => setFiltroDataFinal(e.target.value)}
                className="border border-stone-300 rounded-lg px-3 py-2 text-sm text-stone-700" />
            </div>
          </div>
        </div>

        {/* Navegação por abas */}
        <div className="flex gap-1 border-b border-stone-200">
          {abas.map(aba => (
            <button
              key={aba.id}
              onClick={() => setAbaAtiva(aba.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                abaAtiva === aba.id
                  ? 'border-stone-700 text-stone-800'
                  : 'border-transparent text-stone-500 hover:text-stone-700 hover:border-stone-300'
              }`}
            >
              {aba.label}
            </button>
          ))}
        </div>

        {/* Conteúdo da aba */}
        <div>
          {/* Dashboard */}
          {abaAtiva === 'dashboard' && (
            <div className="flex flex-col gap-6">
              {kpisLoading ? (
                <p className="text-stone-400 text-sm text-center py-12">Carregando KPIs…</p>
              ) : kpis ? (
                <>
                  {/* Botões de exportação Dashboard */}
                  <div className="flex gap-2 justify-end">
                    <button onClick={exportarDashboardExcel} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" /></svg>
                      Excel
                    </button>
                    <button onClick={exportarDashboardPDF} className="flex items-center gap-1.5 px-3 py-1.5 bg-stone-600 text-white text-xs font-medium rounded-lg hover:bg-stone-700">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>
                      PDF
                    </button>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    <KpiCard
                      title="Alertas Superfaturamento"
                      value={kpis.totalAlertasSuperfaturamento}
                      color={kpis.totalAlertasSuperfaturamento > 0 ? 'red' : 'green'}
                      sub={`acima de ${kpis.thresholdSuperfaturamento}% da média`}
                      tooltip={`Quantidade de itens de NF-e cujo preço unitário pago superou em mais de ${kpis.thresholdSuperfaturamento}% a média histórica do mesmo material (por código de catálogo). Requer ao menos 2 registros do material para calcular a média.`}
                    />
                    <KpiCard
                      title="Fornecedores Alertados"
                      value={kpis.fornecedoresAlertados}
                      color={kpis.fornecedoresAlertados > 0 ? 'red' : 'green'}
                      tooltip="Número de fornecedores distintos envolvidos em pelo menos um alerta de superfaturamento. Um mesmo fornecedor pode aparecer em múltiplos alertas, mas é contado uma única vez."
                    />
                    <KpiCard
                      title="Obras com Alertas"
                      value={kpis.obrasAlertadas}
                      color={kpis.obrasAlertadas > 0 ? 'amber' : 'green'}
                      tooltip="Número de obras distintas que possuem ao menos um item com alerta de superfaturamento. Útil para priorizar quais obras investigar primeiro."
                    />
                    <KpiCard
                      title="Excesso Estimado"
                      value={formatCurrency(kpis.valorExcessoEstimado)}
                      color={kpis.valorExcessoEstimado > 0 ? 'red' : 'green'}
                      sub="valor acima da média esperada"
                      tooltip={`Soma de (preço pago − média histórica) × quantidade para todos os itens em alerta. Representa uma estimativa do valor pago a mais em relação ao preço médio esperado. Não significa necessariamente irregularidade — variações de mercado, urgência ou especificações técnicas podem justificar preços acima da média.`}
                    />
                    <KpiCard
                      title="Obras c/ Alta Concentração"
                      value={kpis.obrasComConcentracao}
                      color={kpis.obrasComConcentracao > 0 ? 'amber' : 'green'}
                      sub={`fornecedor com ≥${kpis.thresholdConcentracao}%`}
                      tooltip={`Obras onde um único fornecedor concentra ${kpis.thresholdConcentracao}% ou mais do valor total de NF-e da obra. Alta concentração pode indicar dependência excessiva ou direcionamento de compras. Acesse a aba Concentração para ver o detalhamento.`}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-stone-50 border border-stone-200 rounded-xl p-4 text-sm text-stone-700">
                      <h3 className="font-medium text-stone-800 mb-3">O que analisar primeiro?</h3>
                      <ul className="space-y-2 text-stone-600">
                        {kpis.totalAlertasSuperfaturamento > 0 && (
                          <li className="flex gap-2 items-start">
                            <span className="text-red-500 mt-0.5">●</span>
                            <span>
                              <strong>{kpis.totalAlertasSuperfaturamento} itens</strong> com preço acima de{' '}
                              {kpis.thresholdSuperfaturamento}% da média — acesse a aba <em>Superfaturamento</em>.
                            </span>
                          </li>
                        )}
                        {kpis.obrasComConcentracao > 0 && (
                          <li className="flex gap-2 items-start">
                            <span className="text-amber-500 mt-0.5">●</span>
                            <span>
                              <strong>{kpis.obrasComConcentracao} obras</strong> com fornecedor concentrando{' '}
                              ≥{kpis.thresholdConcentracao}% — acesse <em>Concentração</em>.
                            </span>
                          </li>
                        )}
                        {kpis.totalAlertasSuperfaturamento === 0 && kpis.obrasComConcentracao === 0 && (
                          <li className="text-green-700">Nenhum alerta encontrado com os parâmetros padrão.</li>
                        )}
                      </ul>
                    </div>

                    <div className="bg-stone-50 border border-stone-200 rounded-xl p-4 text-sm text-stone-700">
                      <h3 className="font-medium text-stone-800 mb-3">Metodologia de detecção</h3>
                      <ul className="space-y-1.5 text-stone-600 text-xs">
                        <li><strong>Superfaturamento por preço:</strong> Compara o preço unitário pago a um fornecedor com a média histórica do mesmo material (por código de catálogo). Requer ao menos 2 registros para ter base de comparação.</li>
                        <li className="pt-1"><strong>Concentração de fornecedor:</strong> Identifica obras onde um único fornecedor representa parcela relevante do total de compras, indicando possível dependência ou direcionamento.</li>
                        <li className="pt-1"><strong>Maiores fornecedores:</strong> Ranking de participação por valor de NF-e, por obra ou cliente.</li>
                      </ul>
                    </div>
                  </div>
                </>
              ) : null}
            </div>
          )}

          {abaAtiva === 'materiais' && (
            <MateriaisTab clientes={clientes} obras={obras} filtros={filtros} />
          )}

          {abaAtiva === 'fornecedores' && (
            <FornecedoresTab clientes={clientes} obras={obras} filtros={filtros} />
          )}

          {abaAtiva === 'superfaturamento' && (
            <SuperfaturamentoTab filtros={filtros} />
          )}

          {abaAtiva === 'concentracao' && (
            <ConcentracaoTab filtros={filtros} />
          )}
        </div>
      </div>
    </Template>
  );
};

export default Compliance;
